"use client";
import { motion } from "framer-motion";
import { ContactPageData } from "@/types/index";
import Link from "next/link";

interface ContactInfoClientProps {
  contactPageData: ContactPageData;
  lang: string;
}

export default function ContactInfoClient({ contactPageData, lang }: ContactInfoClientProps) {
  return (
    <div className="w-full lg:max-w-[490px] flex flex-col justify-between">
      <div className="flex flex-col gap-10">
        {/* Section Title */}
        <div className="flex flex-col gap-8">
          <div className="flex items-center gap-2.5">
            <div className="w-[30px] h-[1px] bg-[#727C8D]"></div>
            <span className="text-[#1A1D21] font-inter text-base font-medium leading-4 capitalize">
              {contactPageData?.contact?.info?.eyebrow ||
                "Get In Touch"}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <h1 className="text-[#1A1D21] font-inter text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight tracking-[-1.44px]">
              {contactPageData?.contact?.info?.title ||
                "Begin your journey with Nippon Koei BD"}
            </h1>
          </div>
        </div>

        {/* Office Information */}
        <div className="flex flex-col gap-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="36"
            viewBox="0 0 30 36"
            fill="none"
          >
            <path
              d="M29.3698 12.0834C27.6198 4.38335 20.9031 0.916687 15.0031 0.916687C15.0031 0.916687 15.0031 0.916687 14.9864 0.916687C9.10309 0.916687 2.36976 4.36669 0.619755 12.0667C-1.33024 20.6667 3.93642 27.95 8.70309 32.5334C10.4698 34.2334 12.7364 35.0834 15.0031 35.0834C17.2698 35.0834 19.5364 34.2334 21.2864 32.5334C26.0531 27.95 31.3198 20.6834 29.3698 12.0834ZM15.0031 20.4334C12.1031 20.4334 9.75309 18.0834 9.75309 15.1834C9.75309 12.2834 12.1031 9.93335 15.0031 9.93335C17.9031 9.93335 20.2531 12.2834 20.2531 15.1834C20.2531 18.0834 17.9031 20.4334 15.0031 20.4334Z"
              fill="#1A5852"
            />
          </svg>

          <div className="flex flex-col gap-7">
            <p className="text-[#3D434C] font-inter text-base leading-7">
              {contactPageData?.contact?.info?.address ||
                "Concord Baksh Tower, Unit 5-A & B (5th Floor), Plot # 11-A, Road # 48, Block # CWN (A), Gulshan-2, Dhaka-1212, Bangladesh"}
            </p>

            <div className="w-full h-[1px] bg-[#DEE1E5]"></div>

            {/* Contact Details */}
            <div className="flex flex-col gap-2.5">
              {/* Phone */}
              <div className="flex items-center rounded-lg overflow-hidden">
                <div className="flex items-center justify-center w-[140px] px-6 py-7 bg-[#1A5852] rounded-l-lg">
                  <span className="text-white font-inter text-xl font-medium leading-5 uppercase">
                    {contactPageData?.contact?.info?.phone?.label ||
                      "Phone"}
                  </span>
                </div>
                <div className="flex-1 flex items-center gap-2 px-6 py-4 bg-white rounded-r-lg">
                  <div className="flex items-center justify-center min-w-11 w-11 h-11 bg-[#1A5852] border border-[#DEE1E5] rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M18.3057 15.2746C18.3057 15.5746 18.2391 15.8829 18.0974 16.1829C17.9557 16.4829 17.7724 16.7663 17.5307 17.0329C17.1224 17.4829 16.6724 17.8079 16.1641 18.0163C15.6641 18.2246 15.1224 18.3329 14.5391 18.3329C13.6891 18.3329 12.7807 18.1329 11.8224 17.7246C10.8641 17.3163 9.90573 16.7663 8.95573 16.0746C7.9974 15.3746 7.08906 14.5996 6.2224 13.7413C5.36406 12.8746 4.58906 11.9663 3.8974 11.0163C3.21406 10.0663 2.66406 9.11626 2.26406 8.17459C1.86406 7.22459 1.66406 6.31626 1.66406 5.44959C1.66406 4.88293 1.76406 4.34126 1.96406 3.84126C2.16406 3.33293 2.48073 2.86626 2.9224 2.44959C3.45573 1.92459 4.03906 1.66626 4.65573 1.66626C4.88906 1.66626 5.1224 1.71626 5.33073 1.81626C5.5474 1.91626 5.73906 2.06626 5.88906 2.28293L7.8224 5.00793C7.9724 5.21626 8.08073 5.40793 8.15573 5.59126C8.23073 5.76626 8.2724 5.94126 8.2724 6.09959C8.2724 6.29959 8.21406 6.49959 8.0974 6.69126C7.98906 6.88293 7.83073 7.08293 7.63073 7.28293L6.9974 7.94126C6.90573 8.03293 6.86406 8.14126 6.86406 8.27459C6.86406 8.34126 6.8724 8.39959 6.88906 8.46626C6.91406 8.53293 6.93906 8.58293 6.95573 8.63293C7.10573 8.90793 7.36406 9.26626 7.73073 9.69959C8.10573 10.1329 8.50573 10.5746 8.93906 11.0163C9.38906 11.4579 9.8224 11.8663 10.2641 12.2413C10.6974 12.6079 11.0557 12.8579 11.3391 13.0079C11.3807 13.0246 11.4307 13.0496 11.4891 13.0746C11.5557 13.0996 11.6224 13.1079 11.6974 13.1079C11.8391 13.1079 11.9474 13.0579 12.0391 12.9663L12.6724 12.3413C12.8807 12.1329 13.0807 11.9746 13.2724 11.8746C13.4641 11.7579 13.6557 11.6996 13.8641 11.6996C14.0224 11.6996 14.1891 11.7329 14.3724 11.8079C14.5557 11.8829 14.7474 11.9913 14.9557 12.1329L17.7141 14.0913C17.9307 14.2413 18.0807 14.4163 18.1724 14.6246C18.2557 14.8329 18.3057 15.0413 18.3057 15.2746Z"
                        stroke="white"
                        strokeWidth="1.25"
                        strokeMiterlimit="10"
                      />
                    </svg>
                  </div>
                  <span className="text-[#3D434C] font-inter text-base font-medium leading-5">
                    {contactPageData?.contact?.info?.phone?.value ||
                      "+880 2 2222 95390"}
                  </span>
                </div>
              </div>

              {/* Fax */}
              <div className="flex items-center rounded-lg overflow-hidden">
                <div className="flex items-center justify-center w-[140px] px-6 py-7 bg-[#1A5852] rounded-l-lg">
                  <span className="text-white font-inter text-xl font-medium leading-5 uppercase">
                    {contactPageData?.contact?.info?.fax?.label ||
                      "Fax"}
                  </span>
                </div>
                <div className="flex-1 flex items-center gap-2 px-6 py-4 bg-white rounded-r-lg">
                  <div className="flex items-center justify-center min-w-11 w-11 h-11 bg-[#1A5852] border border-[#DEE1E5] rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5.83333 10C2.5 10 2.5 11.4917 2.5 13.3333V14.1667C2.5 16.4667 2.5 18.3333 6.66667 18.3333H13.3333C16.6667 18.3333 17.5 16.4667 17.5 14.1667V13.3333C17.5 11.4917 17.5 10 14.1667 10C13.3333 10 13.1 10.175 12.6667 10.5L11.8167 11.4C10.8333 12.45 9.16667 12.45 8.175 11.4L7.33333 10.5C6.9 10.175 6.66667 10 5.83333 10Z"
                        stroke="white"
                        strokeWidth="1.25"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M15.8307 9.99996V4.99996C15.8307 3.15829 15.8307 1.66663 12.4974 1.66663H7.4974C4.16406 1.66663 4.16406 3.15829 4.16406 4.99996V9.99996"
                        stroke="white"
                        strokeWidth="1.25"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.78906 7.69202H11.5641"
                        stroke="white"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.10156 5.19202H12.2682"
                        stroke="white"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-[#3D434C] font-inter text-base font-medium leading-5">
                    {contactPageData?.contact?.info?.fax?.value ||
                      "+880 2 2222 95391"}
                  </span>
                </div>
              </div>
            </div>

            <div className="w-full h-[1px] bg-[#DEE1E5]"></div>

            {/* Social Media */}
            <div className="flex items-center gap-2.5">
              {/* Facebook */}
              {contactPageData?.contact?.info?.socialMediaLinks
                ?.fb && (
                <Link
                  href={
                    contactPageData.contact.info.socialMediaLinks.fb
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 border border-[#DEE1E5] rounded-full hover:bg-gray-50 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_3817_2520)">
                      <path
                        d="M16 8C16 11.9931 13.0744 15.3028 9.25 15.9028V10.3125H11.1141L11.4688 8H9.25V6.49937C9.25 5.86656 9.56 5.25 10.5538 5.25H11.5625V3.28125C11.5625 3.28125 10.6469 3.125 9.77156 3.125C7.94438 3.125 6.75 4.2325 6.75 6.2375V8H4.71875V10.3125H6.75V15.9028C2.92563 15.3028 0 11.9931 0 8C0 3.58187 3.58187 0 8 0C12.4181 0 16 3.58187 16 8Z"
                        fill="#1877F2"
                      />
                      <path
                        d="M11.1141 10.3125L11.4688 8H9.25V6.49934C9.25 5.86669 9.55994 5.25 10.5537 5.25H11.5625V3.28125C11.5625 3.28125 10.647 3.125 9.77172 3.125C7.94434 3.125 6.75 4.2325 6.75 6.2375V8H4.71875V10.3125H6.75V15.9028C7.15731 15.9667 7.57475 16 8 16C8.42525 16 8.84269 15.9667 9.25 15.9028V10.3125H11.1141Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3817_2520">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Link>
              )}

              {/* LinkedIn */}
              {contactPageData?.contact?.info?.socialMediaLinks
                ?.linkedIn && (
                <Link
                  href={
                    contactPageData.contact.info.socialMediaLinks
                      .linkedIn
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 border border-[#DEE1E5] rounded-full hover:bg-gray-50 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_3817_3916)">
                      <path
                        d="M14.5155 0H1.48455C0.664654 0 0 0.664654 0 1.48455V14.5155C0 15.3353 0.664654 16 1.48455 16H14.5155C15.3353 16 16 15.3353 16 14.5155V1.48455C16 0.664654 15.3353 0 14.5155 0Z"
                        fill="#0B63BD"
                      />
                      <path
                        d="M5.76634 4.36364C5.76634 4.63334 5.68636 4.89698 5.53652 5.12123C5.38668 5.34548 5.17371 5.52026 4.92454 5.62347C4.67537 5.72668 4.40119 5.75369 4.13667 5.70107C3.87215 5.64845 3.62917 5.51858 3.43846 5.32787C3.24776 5.13717 3.11788 4.89419 3.06527 4.62967C3.01265 4.36515 3.03965 4.09097 3.14286 3.8418C3.24607 3.59262 3.42086 3.37965 3.6451 3.22981C3.86935 3.07998 4.133 3 4.4027 3C4.76436 3 5.11121 3.14367 5.36694 3.3994C5.62267 3.65513 5.76634 4.00198 5.76634 4.36364ZM5.45725 6.63636V12.6609C5.45748 12.7054 5.44894 12.7494 5.4321 12.7905C5.41525 12.8317 5.39045 12.8691 5.35911 12.9006C5.32776 12.9321 5.2905 12.9571 5.24946 12.9742C5.20842 12.9912 5.16442 13 5.11997 13H3.6827C3.63826 13.0001 3.59423 12.9915 3.55314 12.9745C3.51206 12.9575 3.47473 12.9326 3.4433 12.9012C3.41187 12.8698 3.38697 12.8325 3.37002 12.7914C3.35306 12.7503 3.3444 12.7063 3.34452 12.6618V6.63636C3.34452 6.54667 3.38015 6.46065 3.44357 6.39723C3.50699 6.33381 3.59301 6.29818 3.6827 6.29818H5.11997C5.20951 6.29842 5.29529 6.33416 5.35852 6.39755C5.42174 6.46095 5.45725 6.54683 5.45725 6.63636ZM12.97 9.77273V12.6891C12.9701 12.73 12.9621 12.7704 12.9466 12.8082C12.931 12.846 12.9081 12.8803 12.8792 12.9092C12.8503 12.9381 12.816 12.961 12.7782 12.9766C12.7404 12.9922 12.6999 13.0001 12.6591 13H11.1136C11.0727 13.0001 11.0323 12.9922 10.9945 12.9766C10.9567 12.961 10.9224 12.9381 10.8935 12.9092C10.8646 12.8803 10.8417 12.846 10.8261 12.8082C10.8105 12.7704 10.8026 12.73 10.8027 12.6891V9.86273C10.8027 9.44091 10.9263 8.01545 9.69997 8.01545C8.74997 8.01545 8.55634 8.99091 8.51815 9.42909V12.6891C8.51816 12.7708 8.48603 12.8492 8.4287 12.9073C8.37138 12.9655 8.29346 12.9988 8.21179 13H6.71906C6.67828 13 6.63789 12.992 6.60022 12.9763C6.56255 12.9607 6.52834 12.9378 6.49954 12.9089C6.47074 12.88 6.44792 12.8457 6.4324 12.808C6.41687 12.7703 6.40894 12.7299 6.40906 12.6891V6.61C6.40894 6.56921 6.41687 6.52881 6.4324 6.49109C6.44792 6.45337 6.47074 6.41909 6.49954 6.39021C6.52834 6.36133 6.56255 6.33841 6.60022 6.32277C6.63789 6.30714 6.67828 6.29909 6.71906 6.29909H8.21179C8.29425 6.29909 8.37333 6.33185 8.43164 6.39015C8.48994 6.44846 8.5227 6.52754 8.5227 6.61V7.13545C8.87543 6.60636 9.39815 6.19818 10.5136 6.19818C12.9845 6.19818 12.97 8.50545 12.97 9.77273Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3817_3916">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Link>
              )}

              {/* YouTube */}
              {contactPageData?.contact?.info?.socialMediaLinks
                ?.youtube && (
                <Link
                  href={
                    contactPageData.contact.info.socialMediaLinks
                      .youtube
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 border border-[#DEE1E5] rounded-full hover:bg-gray-50 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M15.1909 4.39337C15.0181 3.75076 14.5114 3.2442 13.8689 3.07116C12.6951 2.75 7.99981 2.75 7.99981 2.75C7.99981 2.75 3.30472 2.75 2.13092 3.059C1.50076 3.23185 0.981758 3.75087 0.808906 4.39337C0.5 5.56707 0.5 8.00122 0.5 8.00122C0.5 8.00122 0.5 10.4476 0.808906 11.609C0.981922 12.2515 1.48841 12.7581 2.13101 12.9311C3.31707 13.2524 8 13.2524 8 13.2524C8 13.2524 12.6951 13.2524 13.8689 12.9434C14.5115 12.7704 15.0181 12.2639 15.1911 11.6214C15.4999 10.4476 15.4999 8.01355 15.4999 8.01355C15.4999 8.01355 15.5123 5.56705 15.1909 4.39337ZM6.50492 10.2499V5.75244L10.4093 8.00117L6.50492 10.2499Z"
                      fill="#F61C0D"
                    />
                  </svg>
                </Link>
              )}

              {/* X (Twitter) */}
              {contactPageData?.contact?.info?.socialMediaLinks
                ?.x && (
                <Link
                  href={
                    contactPageData.contact.info.socialMediaLinks.x
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 border border-[#DEE1E5] rounded-full hover:bg-gray-50 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_3817_6632)">
                      <path
                        d="M8 0C3.58187 0 0 3.58187 0 8C0 12.4181 3.58187 16 8 16C12.4181 16 16 12.4181 16 8C16 3.58187 12.4181 0 8 0Z"
                        fill="black"
                      />
                      <path
                        d="M8.87336 7.27598L12.2981 3.29504H11.4865L8.51284 6.75164L6.13779 3.29504H3.39844L6.98999 8.52202L3.39844 12.6967H4.21003L7.35031 9.04637L9.85854 12.6967H12.5979L8.87315 7.27598H8.87336ZM4.50245 3.906H5.749L11.4869 12.1135H10.2403L4.50245 3.906Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3817_6632">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
